import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import type { EngagementType } from '../../graphqlEnums'

export const DismissEngagementMutationGQL = graphql(/* GraphQL */ `
  mutation DismissEngagement($input: DismissEngagementInput!) {
    dismissEngagement(input: $input) {
      __typename
      ... on DismissEngagementSuccess {
        engagement {
          __typename
          id
          status
          dismissedAt
          updatedAt
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type DismissEngagementArgs = {
  engagementId: string
  engagementType: EngagementType
}

type UseDismissEngagementResult = {
  dismissEngagement: (args: DismissEngagementArgs) => Promise<boolean>
  loading: boolean
}

/**
 * Wraps the unified `dismissEngagement` mutation. Kind-agnostic — accepts
 * `mention` or `comment` via `engagementType`. Mirrors `useDismissComment`
 * in shape so the eventual comments cutover slots in cleanly.
 */
export const useDismissEngagement = (): UseDismissEngagementResult => {
  const [mutate, { loading }] = useTypedMutation(
    DismissEngagementMutationGQL,
    (data) => data.dismissEngagement,
    {
      successTypename: 'DismissEngagementSuccess',
      // Resolving a mention removes it from `unreplied`; refresh the counts
      // (mentions have no realtime events to do it for us).
      refetchQueries: ['MentionCounts'],
    },
  )

  const dismissEngagement = async ({
    engagementId,
    engagementType,
  }: DismissEngagementArgs): Promise<boolean> => {
    // The hook is kind-agnostic, so the toast copy has to follow. "Resolved"
    // matches the comments toast (`useDismissComment`) — keep the verb aligned
    // so a shared engagement toast slots in cleanly later.
    const successMessage =
      engagementType === 'comment'
        ? 'Comment resolved successfully'
        : 'Mention resolved successfully'
    const errorMessage =
      engagementType === 'comment'
        ? 'An error occurred while resolving the comment'
        : 'An error occurred while resolving the mention'

    const result = await mutate({
      variables: {
        input: { engagementId, engagementType },
      },
    })

    if (result.success) {
      toast.success(successMessage, { duration: 1_000 })
      return true
    }

    toast.error(errorMessage, { duration: 2_000 })
    return false
  }

  return {
    dismissEngagement,
    loading,
  }
}
